import Vue from 'vue'
import Vuex from 'vuex'

import { getField, updateField } from 'vuex-map-fields';
import { doCalculation, atoVars } from 'insurance-needs-engine';
import { uuid } from 'vue3-uuid';
import axios from 'axios';
import axiosThrottle from 'axios-request-throttle';

import _ from "lodash";

import clientConfig from '../config/client';
import premiumVariables from '../config/premiumVariables.json';
import { calculate as premiumCalculation, benefitPeriodElegibility5, benefitPeriodElegibility65 } from '../config/premiumCalculation';
import Enum from './enum';

Vue.use(Vuex)

const isDev = ["localhost", "lifetime.gbsau.dev"].includes(location.hostname);
const apiPath = `api/insuranceCalculation/${clientConfig.api.platformId}`;
const apiUrl = `https://api.gbsau.${isDev ? "dev" : "com"}/${apiPath}`;
const client = axios.create({ baseURL: apiUrl });
axiosThrottle.use(client, { requestsPerSecond: 1 });

const calcVars = atoVars.calculationVariables;

export default new Vuex.Store({
  state: {
    appVersion: process.env.APP_VERSION || '0',
    debugMode: false,
    enableDisclaimer: false,
    acceptedDisclaimer: false,
    detailedExpenses: false,

    premiumVariables,

    needsDeathOnlyCoverAmount: 0,
    deathOnlyCoverAmount: null,
    needsTPDCoverAmount: 0,
    TPDCoverAmount: null,
    needsIPCoverAmount: 0,
    IPCoverAmount: null,

    dependants: [{"Age": 0, "Amount": 3000, "Error": false}],
    debts: [{"Name":'', "Amount":0, "Error": false}],
    assets: [{"Name":'', "Amount":0, "Error": false}],
    expenses: [{"Name":'', "Amount":0, "Error": false}],

    occupations: [
      {
        type: "professional",
        name: Enum.occupation.PROFESSIONAL,
        html: "",
        desc: [
          "Professional (your work requires a university qualification), administrative, managerial or clerical roles",
          "No regular manual work",
          "Your work doesn’t fall into the “Special Risk” classification"
        ],
        examples: `
          Example occupations: professor/lecturer, lawyer/solicitor, administrator,
          medical doctor, book-keeper, IT systems operator, classroom teacher.
        `,
      },
      {
        type: "light",
        name: Enum.occupation.LIGHT,
        html: "",
        desc: [
          "Regular light manual work (light weight lifting, carrying or stocking only)",
          "No regular use of heavy machinery or heavy equipment"
        ],
        examples: `
          Example occupations: chef, electrician, field surveyor,
          florist, retail, nurse, physical education teacher, plumber
        `,
      },
      {
        type: "heavy",
        name: Enum.occupation.HEAVY,
        html: "",
        desc: [
          "Involves the regular use of heavy machinery or heavy equipment",
          "Manual labour, such as regularly bending, lifting or carrying heavy loads",
          "Driving a motor vehicle for most of the day over short distances"
        ],
        examples: `
          Example occupations: labourer, bricklayer, short distance
          courier/driver, farmer, gardener/landscaper, tailor, wait staff
        `,
      },
      {
        type: "special",
        name: Enum.occupation.SPECIAL,
        html: "",
        desc: [
          "Duties are hazardous (risky or dangerous)",
          `Hazardous locations or working environments such as long-distance driving,
          working at heights, underground, at sea, underwater or in an aircraft`,
          `Work in industries that rapidly change or are unpredictable, for example
          professional sportspeople, entertainers, creative artists and media personalities`
        ],
        examples: `
          Example occupations: crane operator, shipyard worker, long distance
          bus/truck driver, pilot, actor, model, professional sportsperson,
          radio/TV presenter, underground/offshore miner, social media personality.
        `,
      }
    ],

    age: clientConfig.defaults.age,
    annualIncome: clientConfig.defaults.annualIncome,
    gender: clientConfig.defaults.gender,
    coverAmount: clientConfig.defaults.coverAmount,
    benefitPeriod: clientConfig.defaults.benefitPeriod,
    waitingPeriod: clientConfig.defaults.waitingPeriod,

    revision: clientConfig.defaults.revision,

    protectionBenefits: null,
    superBenefits: null,
    unitsOfCover: null,
    costPerPeriod: null,
    costPerYear: null,

    skipCalculations: true,
    resetCoverFromNeeds: false,

    api: {
      userUUID: null,
      path: "/needsQuote",
      client
    },

    data: {
      editAssumptions: {
        // edit assumption values
        wageInflation: calcVars.percentSalaryGrowthRate,
        priceInflation: calcVars.percentConsumerPriceIndex,
        retirementAge: calcVars.retirementAge,
        //TPD
        tpdRepayDebtsLiabilities: false,
        tpdFutureIncomeLevel: 100,
        tpdCeaseAllowanceForFutureIncomeAtAge: 65, // this needs to equal retirement age
        //Income protection
        incomeProtectionPaymentPeriod: 65,
        incomeProtectionPercentage: 70,
        incomeProtectionSuperGuarantee: calcVars.percentSuperannuationContribution,
        // life insurance
        lifeInsurancePaymentPeriod: calcVars.retirementAge,
        lifeInsuranceInvestmentReturn: 2.5,
      },
      stepData: {
        // about
        age: 45,
        gender: 'female',
        familyType: Enum.familyType.SINGLE,
        memberType: Enum.memberType.EMPLOYER,
        occupation: Enum.occupation.PROFESSIONAL,
        // debt
        homeType: Enum.homeType.MORTGAGE,
        rentAmount: 0,
        mortgageAmount: 0,
        //income
        salaryYearly: 35000,
        salaryMonthly: 3000,
        salaryWeekly: 650,
        salaryPeriod: 'Year',
        savings: 0,
        // expenses
        expensesAmountPerMonth: 0,
        disabilityCost: 273000,
        funeralExpenses: 5000,
      },
    },

    Enum: Enum,
    constants: {
      MAX_PERIOD: 13,
      MIN_PERIOD: 0,
      START_SCHOOL_AGE: 5,
      FINISH_SCHOOL_AGE: 19,
      WEEKS_PER_YEAR: 52.14,
    },
    config: {
      livingExpenses: [
        {"value": "Fuel and power", "label": "Fuel and power"},
        {"value": "Food and drink", "label": "Food and drink"},
        {"value": "Clothing and footwear", "label": "Clothing and footwear"},
        {"value": "Medical and health expenses", "label": "Medical and health expenses"},
        {"value": "Insurance", "label": "Insurance"},
        {"value": "Telecommunications", "label": "Telecommunications"},
        {"value": "Transport", "label": "Transport"},
        {"value": "Recreation", "label": "Recreation"},
        {"value": "Other", "label": "Other"},
      ],
      debts: [
        {"value": "Investment loan", "label": "Investment loan"},
        {"value": "Personal loan", "label": "Personal loan"},
        {"value": "Car loan", "label": "Car loan"},
        {"value": "Credit card loan", "label": "Credit card loan"},
        {"value": "Other", "label": "Other"},
      ],
      assets: [
        {"value": "Super", "label": "Super"},
        {"value": "Term deposits", "label": "Term deposits"},
        {"value": "Managed funds", "label": "Managed funds"},
        {"value": "Shares", "label": "Shares"},
        {"value": "Investment property", "label": "Investment property"},
        {"value": "Other assets", "label": "Other assets"},
      ],
      fields: {
        // about
        age: { min: 14, max: 74 },
        dependantsAge: { min: 0, max: 25 },
        // debt
        rentAmount: { min: 1, max: 2000 },
        mortgageAmount: { min: 1, max: 5000000 },
        debts: { min: 0, max: 5000000 },
        // income
        salaryYearly: { min: 35000, max: 422117 },
        salaryMonthly: { min: 3000, max: 35176 },
        salaryWeekly: { min: 650, max: 8117 },
        savings: { min: 0, max: 5000000 },
        assets: { min: 0, max: 5000000 },
        // expenses
        expensesAmountPerMonth: { min: 0, max: 50000 },
        expenses:  { min: 1, max: 50000 },
        disabilityCost: { min: 1, max: 1000000 },
        funeralExpenses: { min: 1, max: 50000 },
        dependantAmount: { min: 1, max: 50000 },
        // cover costs
        TPDCover: { min: 0, max: 3000000 },
        IPCover: { min: 0, max: 29900 }, //Also needs to be capped at 85% of the person’s salary

        // edit assumptions
        inflation: { min: 0, max: 10 },
        investmentReturn: { min: 0, max: 10 },
        retirementAge: { min: 55, max: 75 },
        tpdFutureIncomeLevel: { min: 0, max: 100 },
        incomeProtectionPercentage: { min: 0, max: 85 },
        sgRate: { min: calcVars.percentSuperannuationContribution, max: 25 },
        lifeInsurancePaymentPeriod: { min: 0, max: 65 },
      }
    },
    formatOptions: {
      moneys: {
        numeral: true,
        numeralPositiveOnly: true,
        noImmediatePrefix: false,
        rawValueTrimPrefix: true,
        numeralIntegerScale: 8,
        numeralDecimalScale: 0,
        prefix: '$',
        signBeforePrefix: true
      },
      age: {
        numeral: true,
        numeralPositiveOnly: true,
        numeralIntegerScale: 2,
        numeralDecimalScale: 0
      },
      percentage: {
        numeral: true,
        numeralIntegerScale: 3,
        numeralDecimalScale: 2
      },
      years: {
        numeral: true,
        numeralPositiveOnly: true,
        numeralIntegerScale: 2,
        numeralDecimalScale: 0,
      }
    },
    engineResults: {},
    routeName: null,
    prevTab: null,
  },
  mutations: {
    updatePrevTab(state, value) {
      state.prevTab = value;
    },

    updateSkipCalculations(state, value) {
      state.skipCalculations = value;
      if(value) {
        state.needsDeathOnlyCoverAmount = state.deathOnlyCoverAmount = null;
        state.needsTPDCoverAmount = state.TPDCoverAmount = null;
        state.needsIPCoverAmount = state.IPCoverAmount = null;
      }
    },

    updateResetCoverFromNeeds(state, value) {
      state.resetCoverFromNeeds = value;
    },

    updateAge(state, value) {
      state.age = value;
    },

    updateAnnualIncome(state, value) {
      state.annualIncome = value;
    },

    updateGender(state, value) {
      state.gender = value;
    },

    updateCoverAmount(state, value) {
      state.coverAmount = value;
    },

    updateBenefitPeriod(state, value) {
      state.benefitPeriod = value;
    },

    updateWaitingPeriod(state, value) {
      state.waitingPeriod = value;
    },
    updateProtectionBenefits(state, value) {
      state.protectionBenefits = value;
    },

    updateSuperBenefits(state, value) {
      state.superBenefits = value;
    },

    updateUnitsOfCover(state, value) {
      state.unitsOfCover = value;
    },

    updateCostPerPeriod(state, value) {
      state.costPerPeriod = value;
    },

    updateCostPerYear(state, value) {
      state.costPerYear = value;
    },
    updateDeathOnlyCoverAmount(state, value) {
      state.deathOnlyCoverAmount = value;
    },
    updateTPDCoverAmount(state, value) {
      state.TPDCoverAmount = value;
    },
    updateIPCoverAmount(state, value) {
      state.IPCoverAmount = value;
    },
    updateEnableDisclaimer(state, value) {
      state.enableDisclaimer = value;
    },
    updateAcceptedDisclaimer(state, value) {
      state.acceptedDisclaimer = value;
    },
    updateDetailedExpenses(state, value) {
      state.detailedExpenses = value;
    },
    updateAgeRelatedFieldValues(state) {
      if(state.data.stepData.age < clientConfig.defaults.editAssumptions.retirementAge) {
        state.data.editAssumptions.retirementAge = 67;
      } else if(state.data.stepData.age >= clientConfig.defaults.editAssumptions.retirementAge) {
        state.data.editAssumptions.retirementAge = state.data.stepData.age + 1;
      } else if(state.data.stepData.age == 14 && state.data.stepData.memberType === state.Enum.memberType.PERSONAL) {
        state.data.stepData.memberType = null;
      }
    },
    setLifeInsurancePaymentPeriod(state) {
      state.data.editAssumptions.lifeInsurancePaymentPeriod = state.data.editAssumptions.retirementAge;
    },
    settpdCeaseAllowanceForFutureIncomeAtAge(state) {
      state.data.editAssumptions.tpdCeaseAllowanceForFutureIncomeAtAge = state.data.editAssumptions.retirementAge;
    },
    setDefaultAssumptions(state) {
      state.data.editAssumptions = _.cloneDeep(clientConfig.defaults.editAssumptions);
      state.config.fields = _.cloneDeep(clientConfig.defaults.fields);

      if(state.data.stepData.age >= clientConfig.defaults.editAssumptions.retirementAge) {
        state.data.editAssumptions.retirementAge = state.data.stepData.age + 1;
      }
      state.data.editAssumptions.lifeInsurancePaymentPeriod = state.data.editAssumptions.retirementAge;

      // set tpd cease allowance for future income at age based on retirement age. must be equal
      state.data.editAssumptions.tpdCeaseAllowanceForFutureIncomeAtAge = state.data.editAssumptions.retirementAge;
    },
    setDefaultData(state) {
      state.detailedExpenses = false;
      state.dependants = _.cloneDeep(clientConfig.defaults.dependants);
      state.debts = _.cloneDeep(clientConfig.defaults.debts);
      state.assets = _.cloneDeep(clientConfig.defaults.assets);
      state.expenses = _.cloneDeep(clientConfig.defaults.expenses);
      state.data.stepData = _.cloneDeep(clientConfig.defaults.stepData);

      if (clientConfig.defaults.occupations) {
        state.occupations = _.cloneDeep(clientConfig.defaults.occupations);
      }

      state.api.userUUID = uuid.v4();
    },
    setDefaults() {
      this.commit("setDefaultAssumptions");
      this.commit("setDefaultData");
    },
    updateRouteName(state, value) {
      state.routeName = value;
    },
    updateField,
  },
  actions: {
    getEngineResults() {
      var assetsArr = [];
      assetsArr = this.state.assets.slice(0);
      assetsArr.push({"Name": "Savings", "Amount": this.state.data.stepData.savings});

      // EXPENSES
      // work out education costs - per dependant
      var expArr = [];
      if(this.state.data.stepData.familyType === this.state.Enum.familyType.SINGLE_DEPENDANT || this.state.data.stepData.familyType === this.state.Enum.familyType.COUPLE_DEPENDANT) {
        this.state.dependants.forEach(dep => {
          var age = dep.Age;
          // calculate deferral period and payment period based on child's age
          var deferralPeriod = age < this.state.constants.START_SCHOOL_AGE + 1 ? this.state.constants.START_SCHOOL_AGE - age : this.state.constants.MIN_PERIOD;
          var paymentPeriod = age > this.state.constants.START_SCHOOL_AGE ? this.state.constants.FINISH_SCHOOL_AGE - age : this.state.constants.MAX_PERIOD;
          var newDep = {
                    "Name": "Education costs",
                    "Cost_pa": dep.Amount,
                    "DeferralPeriod": deferralPeriod,
                    "PaymentPeriod": paymentPeriod,
                    "InflationRate_pa": this.state.data.editAssumptions.priceInflation / 100,
                    "InvestmentReturn_pa": this.state.data.editAssumptions.lifeInsuranceInvestmentReturn / 100
                  };
          expArr.push(newDep);
        });
      }

      if (this.state.data.stepData.familyType !== this.state.Enum.familyType.SINGLE ||
        this.state.data.stepData.familyTypeSingleExpenses) {
        var livingExp = {};

        if(this.state.detailedExpenses) {
          var total = 0;
          this.state.expenses.forEach(expense => {
            total += expense.Amount;
          })
          livingExp = {
            "Name": "Living",
            "Cost_pa": total * 12,
            "DeferralPeriod": 0,
            "PaymentPeriod": this.state.data.editAssumptions.lifeInsurancePaymentPeriod - this.state.data.stepData.age,
            "InflationRate_pa": this.state.data.editAssumptions.priceInflation / 100,
            "InvestmentReturn_pa": this.state.data.editAssumptions.lifeInsuranceInvestmentReturn / 100
          };
        }
        else {
          livingExp = {
            "Name": "Living",
            "Cost_pa": this.state.data.stepData.expensesAmountPerMonth * 12,
            "DeferralPeriod": 0,
            "PaymentPeriod": this.state.data.editAssumptions.lifeInsurancePaymentPeriod - this.state.data.stepData.age,
            "InflationRate_pa": this.state.data.editAssumptions.priceInflation / 100,
            "InvestmentReturn_pa": this.state.data.editAssumptions.lifeInsuranceInvestmentReturn / 100
          };
        }
        expArr.push(livingExp);

        if(this.state.data.stepData.homeType === this.state.Enum.homeType.RENT) {
          var rentExp = {
            "Name": "Rent",
            "Cost_pa": this.state.data.stepData.rentAmount * this.state.constants.WEEKS_PER_YEAR,
            "DeferralPeriod": 0,
            "PaymentPeriod": this.state.data.editAssumptions.lifeInsurancePaymentPeriod - this.state.data.stepData.age,
            "InflationRate_pa": this.state.data.editAssumptions.priceInflation / 100,
            "InvestmentReturn_pa": this.state.data.editAssumptions.lifeInsuranceInvestmentReturn / 100
          };
          expArr.push(rentExp);
        }
      }

      // mortgage
      var propertyArr = [];
      if(this.state.data.stepData.homeType === this.state.Enum.homeType.MORTGAGE) {
        propertyArr.push({
          "Name": "Main residence","MarketValue": 0,"Mortgage": this.state.data.stepData.mortgageAmount,
          "RepayMortgageWithLifeInsurance": true,"SellAndUseNetProceeds": false
        });
      }

      var obj = {
        "Age": this.state.data.stepData.age,
        "Salary":  this.state.data.stepData.salaryPeriod === "Month" ? this.state.data.stepData.salaryMonthly * 12 :
                    this.state.data.stepData.salaryPeriod === "Week" ? this.state.data.stepData.salaryWeekly * this.state.constants.WEEKS_PER_YEAR :
                    this.state.data.stepData.salaryYearly,
        "RetirementAge": this.state.data.editAssumptions.retirementAge,
        "LifeInsuranceInputs": {
        "ImmediateExpense": this.state.data.stepData.funeralExpenses,
        "ExistingCover": 0
        },
        "IncomeProtectionInputs": {
          "BenefitLevel_Income": this.state.data.editAssumptions.incomeProtectionPercentage / 100, // edit assumptions
          "BenefitLevel_Superannuation": this.state.data.editAssumptions.incomeProtectionSuperGuarantee / 100,
          "PaymentPeriod": this.state.data.editAssumptions.incomeProtectionPaymentPeriod, // comes from edit assupmtions
          "ExistingCover": 0,
          "MaximumCoverPerMonth": this.state.data.editAssumptions.MaximumCoverPerMonth,
        },
        "TPDInputs":
        {
          "ImmediateExpense": this.state.data.stepData.disabilityCost, //  cost of disability
          "FutureIncomeLevel": this.state.data.editAssumptions.tpdFutureIncomeLevel / 100, // not config - need to expose in edit assumptions. min val: income protection benefit inome percentage. max val 100%
          "FutureIncomeRelativeToPreInjuryAfterTaxSalary": true, // dont need to expose
          "CeaseAllowanceForFutureIncomeAtAge": this.state.data.editAssumptions.tpdCeaseAllowanceForFutureIncomeAtAge, // we need to expose this in edit assumptions - default to retirement age
          "Inflation": this.state.data.editAssumptions.wageInflation / 100, // edit assumptions
          "InvestmentReturn": this.state.data.editAssumptions.lifeInsuranceInvestmentReturn / 100, // edit assumptions
          "ExistingCover": 0,
          "RepayDebtsLiabilities": this.state.data.editAssumptions.tpdRepayDebtsLiabilities  // edit assumptions TPD to include debt and liabilies
        },
        "Debts": this.state.debts,
        "Assets": assetsArr,
        "LifeInsuranceFutureExpenses": expArr,
        // the default payment period for living expenses.  default: min(10, retirementAge - currentAge). min: 0 - max: retirementAge - currentAge.
        "TPDFutureExpenses": [], // this was going to be used for homemaker - no longer required
        "Properties": propertyArr //we will use this for home with mortgage
      };
      console.log("data:", obj);
      var res = doCalculation(obj);
      console.log(res);
      // Round up to nearest ?
      res.IncomeProtectionInsurance.IPInsuranceNeed = (res.IncomeProtectionInsurance.BenefitLevel_Income_per_month * 12) + (res.IncomeProtectionInsurance.BenefitLevel_Superannuation_per_month * 12);
      if(!this.state.data.stepData.fixedNotRounded) {
        res.LifeInsurance.LifeInsuranceNeed = Math.ceil(res.LifeInsurance.LifeInsuranceNeed/1000)*1000;
        res.TPDInsurance.TPDInsuranceNeed = Math.ceil(res.TPDInsurance.TPDInsuranceNeed/1000)*1000;
        res.IncomeProtectionInsurance.IPInsuranceNeed = Math.ceil(res.IncomeProtectionInsurance.IPInsuranceNeed/100)*100;
      }
      this.state.engineResults = res;
      // Set default needs values for the Cover cost calculations
      if(!this.state.skipCalculations && this.state.resetCoverFromNeeds) {
        this.state.needsDeathOnlyCoverAmount = this.state.deathOnlyCoverAmount = Math.max(Math.round(res.LifeInsurance.LifeInsuranceNeed),0);
        this.state.needsTPDCoverAmount = this.state.TPDCoverAmount = Math.min(Math.max(Math.round(res.TPDInsurance.TPDInsuranceNeed),0), this.state.config.fields.TPDCover.max);
        this.state.needsIPCoverAmount = this.state.IPCoverAmount = Math.round(res.IncomeProtectionInsurance.IPInsuranceNeed / (this.state.data.stepData.IPCoverMonthly ? 12 : 1));
      }
    },

    validateSalary({ state }, refs) {
      const stepData = state.data.stepData;
      const isWeekly = stepData.salaryPeriod === "Week";
      const isMonthly = stepData.salaryPeriod === "Month";
      if((isWeekly ? stepData.salaryWeekly : isMonthly ? stepData.salaryMonthly : stepData.salaryYearly)) {
        const input = (isWeekly ? refs.salaryWeekly : isMonthly ? refs.salaryMonthly : refs.salaryYearly).$el;
        input.focus(); input.blur();
      }
    }
  },
  getters: {
    appVersion: state => {
      return state.appVersion;
    },
    insuranceData: state => {
      return state.insuranceData;
    },
    premiumVariables: state => {
      return state.premiumVariables;
    },
    acceptedDisclaimer: state => {
      return state.acceptedDisclaimer;
    },
    familyType: state => {
      return state.familyType;
    },
    memberType: state => {
      return state.memberType;
    },
    occupation: state => {
      return state.occupation;
    },
    occupations: state => {
      return state.occupations;
    },
    Enum: state => {
      return state.Enum;
    },
    data: state => {
      return state.data;
    },
    api: state => {
      return state.api;
    },
    config: state => {
      return state.config;
    },
    detailedExpenses: state => {
      return state.detailedExpenses;
    },
    dependants: state => {
      return state.dependants;
    },
    debts: state => {
      return state.debts
    },
    expenses: state => {
      return state.expenses;
    },
    assets: state => {
      return state.assets;
    },
    engineResults: state => {
      return state.engineResults;
    },
    formatOptions: state => {
      return state.formatOptions;
    },
    editAssumptions: state => {
      return state.data.editAssumptions;
    },
    pageContent: () => {
      return clientConfig.content;
    },
    enableDisclaimer: state => {
      return state.enableDisclaimer;
    },
    calculatedPremiums: state => context => {
      return premiumCalculation(state, context);
    },
    benefitPeriodElegibility5: state => {
      return benefitPeriodElegibility5(state.data.stepData);
    },
    benefitPeriodElegibility65: state => {
      return benefitPeriodElegibility65(state.data.stepData);
    },
    getField,
  }
})
